import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FacilityObject } from 'src/app/core/services/facility.model';
import { environment } from '../../../../environments/environment';
import { HaloLoaderService } from '../../../core/halo-loader/halo-loader.service';
import { RootScopeDeclare } from '../../../core/rootscope-declare';
import { FacilityService } from '../../../core/services/facility.service';
import { RootScopeService } from '../../../core/shared-service/root-scope.service';
import { CommonUtil } from '../../../core/Utils/common-util';
import { DeprecatedConsole, DeprecatedFeatureType } from '../../deprecated-banner/deprecated-banner.interface';
import {
  equipmentDetailsObject,
  equipmentObject,
  nonCardioEquipmentBaseObject,
  nonCardioEquipmentDetails,
} from '../../services/equipment.model';
import { EquipmentService } from '../../services/equipment.service';

@Component({
  selector: 'app-equipment',
  templateUrl: './equipment.component.html',
  styleUrls: ['./equipment.component.scss'],
})
export class EquipmentComponent implements OnInit {
  rootScopeData: RootScopeDeclare;
  currentFacility: FacilityObject;
  equipmentListObj: equipmentObject;
  listOfEquipments: equipmentDetailsObject[] = [];
  listOfNonCardioEquipments: nonCardioEquipmentDetails[] = [];
  facilityhourstxt: string;
  showFacSuccessPopup = false;
  deprecatedBannerTitle: string;
  deprecatedBannerDescription: string;
  deprecatedConsoles: DeprecatedConsole[] = [];
  deprecatedFeatureType = DeprecatedFeatureType.EquipmentSupportUpdate;
  otherData = {
    screenSize: '',
    facilityHours: '',
    isRTL: false,
  };

  cloudBasePath: string;

  // subscriptions
  private _facilityOnChange: Subscription;
  hasFullPermission: boolean;

  constructor(
    private _rootScopeService: RootScopeService,
    private _facilityService: FacilityService,
    private _router: Router,
    private _cUtil: CommonUtil,
    private _equipmentService: EquipmentService,
    private _loader: HaloLoaderService
  ) {
    this.setDeprecatedFeatureBanner();
  }

  getRootScopeData(): void {
    this._rootScopeService.getRootScope().subscribe((rootScopeData) => (this.rootScopeData = rootScopeData));
  }

  setPageName(): void {
    this.rootScopeData.pageName = 'Facility - Equipment';
  }

  ngOnInit(): void {
    this.getRootScopeData();
    this.setPageName();
    this.currentFacility = this._facilityService.selectedFacility();
    this._facilityOnChange = this._facilityService.selectedFacilitySource.subscribe((res) => {
      this.currentFacility = res;
      this.setEquipmentDetails();
    });
    this.cloudBasePath = environment.cloudBasePath;
    this.setEquipmentDetails();
  }

  ngOnDestroy() {
    // destroy subscriber
    if (this._facilityOnChange) {
      this._facilityOnChange.unsubscribe();
    }
  }

  private setDeprecatedFeatureBanner(): void {
    this.deprecatedBannerTitle = this._cUtil.translateText('DEPRECATED.EQUIPMENT.BANNER.TITLE');
    this.deprecatedBannerDescription = this._cUtil.translateText('DEPRECATED.EQUIPMENT.BANNER.DESCRIPTION');
  }

  setEquipmentDetails(): void {
    if (this.rootScopeData.screenSize) {
      this.otherData.screenSize = this.rootScopeData.screenSize;
      this.otherData.isRTL = this.rootScopeData.isRTL;
    }
    if (this.currentFacility['summary']) {
      this.facilityhourstxt = this._cUtil.getFormattedFacilityHours(this.currentFacility.summary);
      this.otherData.facilityHours = this.facilityhourstxt;
    }

    this.hasFullPermission = this.currentFacility['summary'].userRights.facility === 'F' ? true : false;
    this.fetchEquipmentList();

    this._cUtil.scrollTop();
  }

  fetchEquipmentList(): void {
    if (this.currentFacility.facilityId) {
      this._loader.startProcess();
      this.otherData.screenSize = this.rootScopeData.screenSize;
      this._equipmentService.getEquipmentData(this.currentFacility.facilityId).subscribe(
        (data: equipmentObject) => {
          this.listOfEquipments = [];
          this.listOfNonCardioEquipments = [];

          this.equipmentListObj = data;

          if (data['deprecatedEquipmentVO']) {
            if (data.deprecatedEquipmentVO.hasDeprecatedEquipment) {
              this.deprecatedConsoles = data.deprecatedEquipmentVO.consoleTypeList;
            }
          }

          if (data['equipmentDetails']) {
            this.listOfEquipments = data.equipmentDetails.filter((a) => a.assetDetails && a.assetDetails.length > 0);
          }

          if (data['connectedStrengthEquipmentDetails']) {
            //Below code is transforming the array type of json.
            data.connectedStrengthEquipmentDetails.map(
              (val) =>
                (val.connectedStrengthAssetDetailsVOList = val.hasOwnProperty('connectedStrengthAssetDetailsVOList')
                  ? Array.isArray(val.connectedStrengthAssetDetailsVOList)
                    ? val.connectedStrengthAssetDetailsVOList
                    : [val.connectedStrengthAssetDetailsVOList]
                  : [])
            );

            const connectedStrengthEquipmentList = data.connectedStrengthEquipmentDetails.filter(
              (a) => a.connectedStrengthAssetDetailsVOList && a.connectedStrengthAssetDetailsVOList.length > 0
            );

            const nonCardioStrengthEquipmentList = connectedStrengthEquipmentList.map((val) => {
              return {
                nonCardioAssetDetailsObject: val.connectedStrengthAssetDetailsVOList,
                connectedCount: val.connectedCount,
                nonConnectedCount: val.nonConnectedCount,
                activityId: val.activityId,
                equipmentType: 'strength',
                isOpen: false,
                columnList: ['Product Information', 'Name', 'Status', 'Battery Level'],
              };
            });

            nonCardioStrengthEquipmentList.forEach((val) => {
              this.listOfNonCardioEquipments.push(val);
            });
          }

          if (data['smartInfrastructureEquipmentDetails']) {
            data.smartInfrastructureEquipmentDetails = Array.isArray(data.smartInfrastructureEquipmentDetails)
              ? data.smartInfrastructureEquipmentDetails
              : [data.smartInfrastructureEquipmentDetails];
            //Below code is transforming the array type of json.
            data.smartInfrastructureEquipmentDetails.map((val) => {
              (val.gatewayAssetDetailsVOList = val.hasOwnProperty('gatewayAssetDetailsVOList')
                ? Array.isArray(val.gatewayAssetDetailsVOList)
                  ? val.gatewayAssetDetailsVOList
                  : [val.gatewayAssetDetailsVOList]
                : []),
                (val.waspAssetDetailsVOList = val.hasOwnProperty('waspAssetDetailsVOList')
                  ? Array.isArray(val.waspAssetDetailsVOList)
                    ? val.waspAssetDetailsVOList
                    : [val.waspAssetDetailsVOList]
                  : []);
            });
            const nonCardioAssetDetails: nonCardioEquipmentBaseObject[] = [];
            data.smartInfrastructureEquipmentDetails.forEach((details) => {
              details.gatewayAssetDetailsVOList.forEach((val) => {
                const obj: nonCardioEquipmentBaseObject = {} as nonCardioEquipmentBaseObject;
                obj.facilityId = val.facilityId;
                obj.nickName = val.nickName;
                obj.imageUrl = val.imageUrl;
                obj.isConnected = val.isConnected;
                if (val.updatedTimestamp) {
                  const updatedTimestamp = new Date(val.updatedTimestamp);
                  obj.updatedTimestamp =
                    'Last connected ' +
                    updatedTimestamp.toLocaleString('default', { month: 'short' }) +
                    ' ' +
                    updatedTimestamp.getDate() +
                    ', ' +
                    updatedTimestamp.getFullYear() +
                    ', ' +
                    updatedTimestamp.getHours() +
                    ':' +
                    updatedTimestamp.getMinutes();
                }
                obj.equipmentSerialNumber = val.gatewaySerial;
                obj.nameEditable = val.nameEditable;
                obj.equipmentModel = 'gateway';
                nonCardioAssetDetails.push(obj);
              });
              details.waspAssetDetailsVOList.forEach((val) => {
                const obj: nonCardioEquipmentBaseObject = {} as nonCardioEquipmentBaseObject;
                obj.facilityId = val.facilityId;
                obj.nickName = val.nickName;
                obj.imageUrl = val.imageUrl;
                obj.isConnected = val.isConnected;
                if (val.updatedTimestamp) {
                  const updatedTimestamp = new Date(val.updatedTimestamp);
                  obj.updatedTimestamp =
                    'Last connected ' +
                    updatedTimestamp.toLocaleString('default', { month: 'short' }) +
                    ' ' +
                    updatedTimestamp.getDate() +
                    ', ' +
                    updatedTimestamp.getFullYear() +
                    ', ' +
                    updatedTimestamp.getHours() +
                    ':' +
                    updatedTimestamp.getMinutes();
                }
                obj.equipmentSerialNumber = val.waspsSerial;
                obj.nameEditable = val.nameEditable;
                obj.equipmentModel = 'wasp';
                nonCardioAssetDetails.push(obj);
              });

              if (nonCardioAssetDetails.length > 0) {
                this.listOfNonCardioEquipments.push({
                  nonCardioAssetDetailsObject: nonCardioAssetDetails,
                  connectedCount: details.connectedCount,
                  nonConnectedCount: details.nonConnectedCount,
                  activityId: details.activityId,
                  equipmentType: 'smartInfra',
                  isOpen: false,
                  columnList: ['Product Information', 'Name', 'Status'],
                });
              }
            });
          }
        },
        (error) => {
          console.error('API failed.');
          this._loader.endProcess();
        },
        () => {
          this._loader.endProcess();
        }
      );
    }
  }

  goToAddEquipment(): void {
    if (this.rootScopeData.isPageHasUnsavedChanges) {
      this.rootScopeData.showUnSavedChangesModal = true;
      if (this.equipmentListObj.hasOldEquipment === 'true') {
        this.rootScopeData.pathToNavigate = '/facility/equipment/add';
        this.rootScopeData.pathToNavigateQueryParam = { state: { action: '1' } };
      } else {
        this.rootScopeData.pathToNavigate = '/facility/equipment/add';
      }
    } else {
      if (this.equipmentListObj.hasOldEquipment === 'true') {
        this._router.navigateByUrl('/facility/equipment/add', { state: { action: '1' } });
      } else {
        this._router.navigate(['/facility/equipment/add']);
      }
    }
  }

  updateFacilityEquipment(): void {
    this._facilityService.reset();
    this._facilityService.get().then(
      (data) => {
        this.currentFacility = this._facilityService.selectedFacility();
        // this._router.navigate(["/facility/equipment"]);
      },
      (error) => {
        console.error('API failed.');
      }
    );
  }

  loadSmartInfraEquipmentDummyData() {
    const tmp = [
      {
        smartInfraAssetDetailsVOList: [
          {
            facilityId: 1,
            equipmentSerialNumber: 'Gateway Serial Number',
            machineModelId: 2222,
            machineModelName: 'Demo Stength Equipment 1',
            machineModelDescription: 'Demo Stength Equipment 1',
            imageUrl: 'https://s3.amazonaws.com/LFconnectApp/images/insignia/Product_INSIGNIA_Back_Ext.png',
            nickName: '',
            batteryLevel: 80,
            isConnected: 'true',
            lastWorkoutDateMillis: 12909,
          },
          {
            facilityId: 2,
            equipmentSerialNumber: 'Wasp Serial Number',
            machineModelId: 3333,
            machineModelName: 'Demo Stength Equipment 2',
            machineModelDescription: 'Demo Stength Equipment 2',
            imageUrl: 'https://s3.amazonaws.com/LFconnectApp/images/insignia/Product_INSIGNIA_Back_Ext.png',
            nickName: '',
            batteryLevel: 20,
            isConnected: 'false',
            lastWorkoutDateMillis: 12909,
          },
        ],
        connectedCount: 1,
        nonConnectedCount: 1,
        activityId: 'SMART Infrastructure',
        columnList: ['Product Information', 'Name', 'Status'],
      },
    ];
    return tmp;
  }
}
